// components/Form/MultiSelectDropdown.tsx
import { useState, useRef, useEffect } from 'react';
import { FormField } from '../FormField';
import { DropdownOption } from '../fixedDropdown/FixedDropdown';
import './multiselectDropdown.css'
 
type MultiSelectDropdownProps = {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: DropdownOption[];
  error?: string;
  placeholder?: string;
  className?: string;
  maxHeight?: string;
};
 
export const MultiSelectDropdown = ({
  label,
  value,
  onChange,
  options,
  error,
  placeholder = 'Seleccionar opciones...',
  className,
  maxHeight = '200px'
}: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
	const handleClickOutside = (event: MouseEvent) => {
	  if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
		setIsOpen(false);
	  }
	};
 
	document.addEventListener('mousedown', handleClickOutside);
	return () => {
	  document.removeEventListener('mousedown', handleClickOutside);
	};
  }, []);
 
  const handleOptionToggle = (optionId: string) => {
	const isSelected = value.includes(optionId);
	 
	if (isSelected) {
	  // Remover de la selección
	  onChange(value.filter(id => id !== optionId));
	} else {
	  // Agregar a la selección
	  onChange([...value, optionId]);
	}
  };
 
  const handleSelectAll = () => {
	if (value.length === options.length) {
	  // Si todos están seleccionados, deseleccionar todos
	  onChange([]);
	} else {
	  // Seleccionar todos
	  onChange(options.map(option => option.id));
	}
  };
 
  const getSelectedLabels = () => {
	if (value.length === 0) return placeholder;
	if (value.length === 1) {
	  const option = options.find(opt => opt.id === value[0]);
	  return option?.label || '';
	}
	return `${value.length} opciones seleccionadas`;
  };
 
  const toggleDropdown = () => {
	setIsOpen(!isOpen);
  };
 
  return (
	<FormField label={label} error={error} className={className}>
	  <div className="multiselect-container" ref={containerRef}>
		<div 
		  className="multiselect-display"
		  onClick={toggleDropdown}
		  tabIndex={0}
		  role="button"
		  aria-expanded={isOpen}
		  aria-haspopup="listbox"
		>
		  <span className="multiselect-text">{getSelectedLabels()}</span>
		  <div className={`multiselect-arrow ${isOpen ? 'open' : ''}`}>▼</div>
		</div>
		 
		{isOpen && (
		  <div className="multiselect-options" style={{ maxHeight }}>
			<div 
			  className="multiselect-option multiselect-select-all"
			  onClick={handleSelectAll}
			>
			  <input 
				type="checkbox" 
				checked={value.length === options.length && options.length > 0}
				onChange={() => {}} // Controlled by onClick
				className="multiselect-checkbox"
			  />
			  <span>Seleccionar todo</span>
			</div>
			 
			{options.map((option) => {
			  const isSelected = value.includes(option.id);
			  return (
				<div
				  key={option.id}
				  className={`multiselect-option ${isSelected ? 'selected' : ''}`}
				  onClick={() => handleOptionToggle(option.id)}
				>
				  <input 
					type="checkbox" 
					checked={isSelected}
					onChange={() => {}} // Controlled by onClick
					className="multiselect-checkbox"
				  />
				  <span>{option.label}</span>
				</div>
			  );
			})}
		  </div>
		)}
	  </div>
	</FormField>
  );
};